import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { createOrUpdatePatient, createPatientSession, createVisit, searchPatients } from "../../api/services/clinic/patientManagement";
import { useInfiniteScroll } from "../../utils/paginationUtils";
import Button from "../../components/Button";
import InputField from "../../components/InputField";
import MenuList from "../../components/MenuList";
import { useToast } from "../../contexts/ToastContext";
import SelectPatientTypeModal from '../../pages/patient_management/SelectPatientTypeModal';
import ScreeningModal from '../../pages/patient_management/ScreeningModal';
import handleClickOutsideElement from '../../utils/handleClickOutsideElement';
import { buildScreeningPayload } from '../../utils/screening';

const CreateOrEditPatientModal = lazy(() => import('../../pages/CreateOrEditPatientModal'));

/**
 * PatientActionsPanel
 *
 * A widget that provides patient-related actions:
 * - search patient
 * - register new patient.
 */
export default function PatientActionPanel({ onSuccess }) {
    const [searchKeyword, setSearchKeyword] = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");
    const [isCreateOrEditPatientModalOpen, setIsCreateOrEditPatientModalOpen] = useState(false);
    const [isSelectPatientTypeModalOpen, setIsSelectPatientTypeModalOpen] = useState(false);
    const [isScreeningModalOpen, setIsScreeningModalOpen] = useState(false);
    const [patientId, setPatientId] = useState("");
    const [patientType, setPatientType] = useState("thai");
    const [pendingSessionId, setPendingSessionId] = useState("");
    const [screeningInitialValues, setScreeningInitialValues] = useState({});

    const searchContainerRef = useRef(null);
    const [shouldShowSearchResults, setShouldShowSearchResults] = useState(false);

    const navigate = useNavigate();
    const { showSuccessToast, showErrorToast } = useToast();

    // Debounce search keyword
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedKeyword(searchKeyword);
        }, 300);

        return () => clearTimeout(handler);
    }, [searchKeyword]);

    // Create a paginated search function
    const fetchSearchResults = useCallback(
        async ({ page }) => {
            const response = await searchPatients(debouncedKeyword, { page, pageSize: 10 });
            return ({
                results: response.results || [],
                total_pages: response.total_pages ?? 1,
            });
        },
        [debouncedKeyword]
    );

    // Use infinite scroll hook
    const {
        items: searchResults,
        isLoading,
        scrollRef: searchResultsScrollRef
    } = useInfiniteScroll(
        fetchSearchResults,
        {
            enabled: shouldShowSearchResults && debouncedKeyword.trim() !== '',
            extraParams: { keyword: debouncedKeyword },
            bottomThreshold: 50,
            onError: (error) => showErrorToast("ไม่สามารถค้นหาข้อมูลผู้ป่วยได้", error),
        }
    );

    handleClickOutsideElement(searchContainerRef, () => setShouldShowSearchResults(false));

    function handleSelectPatient(patient) {
        navigate(`/patient-management/profile/${patient.id}`);
    }

    function buildSearchResults() {
        return <div>
            {(isLoading || searchResults.length > 0) && shouldShowSearchResults && debouncedKeyword.trim() !== '' && (
                <div
                    className="dropdown-list flex flex-col absolute right-0"
                    role="listbox"
                >
                    <div
                        ref={searchResultsScrollRef}
                        className="custom-scrollbar overflow-y-auto max-h-96"
                    >
                        {isLoading && searchResults.length === 0 && (
                            <MenuList label="กำลังค้นหา..."></MenuList>
                        )}

                        {searchResults.map((p) => (
                            <MenuList
                                key={p.id}
                                id={p.id}
                                label={`${p.patient_name} [${p.hn}]`}
                                description={p.id_number_display ?? p.id_number ?? '-'}
                                onClick={() => handleSelectPatient(p)}>
                            </MenuList>
                        ))}

                        {isLoading && searchResults.length > 0 && (
                            <MenuList label="กำลังโหลดเพิ่มเติม..." />
                        )}
                    </div>
                </div>
            )}
        </div>
    }

    function buildSearchPatient() {
        return <div className="relative w-[360px]" ref={searchContainerRef}>
            <InputField
                name="search-patient"
                type="text"
                placeholder="ชื่อ, HN, โทรศัพท์, เลขประจำตัวประชาชน"
                icon="search"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onFocus={() => setShouldShowSearchResults(true)}
            />
            {buildSearchResults()}
        </div>;
    }

    function resetRegistrationFlow() {
        setPendingSessionId("");
        setPatientId("");
        setPatientType("thai");
        setScreeningInitialValues({});
    }

    async function initializePatientSession() {
        try {
            const response = await createPatientSession();
            if (!response?.session_id) {
                throw new Error("session_id is required");
            }
            setPendingSessionId(response.session_id);
            return response.session_id;
        } catch (error) {
            showErrorToast("ไม่สามารถเริ่ม session ผู้ป่วยได้", error);
            return null;
        }
    }

    async function onClickRegister() {
        const sessionId = await initializePatientSession();
        if (!sessionId) return;
        setIsSelectPatientTypeModalOpen(true);
    }

    function handleSelectPatientType(type) {
        setPatientType(type);
        setIsSelectPatientTypeModalOpen(false);
        setIsCreateOrEditPatientModalOpen(true);
    }

    function buildCreatePatientButton() {
        return <Button
            id="create-patient"
            hierarchy="primary"
            size="md"
            text="ลงทะเบียนผู้ป่วย"
            leadingIcon="user-add"
            onClick={() => onClickRegister()}>
        </Button>
    }

    return (<div className="flex gap-3">
        {buildSearchPatient()}
        {buildCreatePatientButton()}
        <SelectPatientTypeModal
            isOpen={isSelectPatientTypeModalOpen}
            onNext={handleSelectPatientType}
            onClose={() => setIsSelectPatientTypeModalOpen(false)}
        />
        {isCreateOrEditPatientModalOpen && (
            <Suspense fallback={null}>
                <CreateOrEditPatientModal
                    modalType="create"
                    patientType={patientType}
                    isOpen={isCreateOrEditPatientModalOpen}
                    onCancel={() => {
                        setIsCreateOrEditPatientModalOpen(false);
                        resetRegistrationFlow();
                    }}
                    onSave={async (data) => {
                        const payload = {
                            "patient_type": patientType,
                            "id": null,
                            "id_card": (!data["noIdentifierDetail"] && data["identifierType"] === "id_card") ? (data["identifierDetail"] || "") : null,
                            "passport_number": (!data["noIdentifierDetail"] && data["identifierType"] === "passport_number") ? (data["identifierDetail"] || "") : null,
                            "photo": data["picture"] || "",
                            "prefix_name": data["prefix"] || "",
                            "first_name": data["firstName"] || "",
                            "last_name": data["lastName"] || "",
                            "has_identity_number": !data["noIdentifierDetail"],
                            "gender": data["gender"] || "",
                            "birth_date": data["birthDate"] || null,
                            "phone_number": data["phone"] || "",
                            "house_number": data["houseNumber"] || "",
                            "address_detail": data["addressDetail"] || "",
                            "province_code": data["provinceCode"] || "",
                            "district_code": data["districtCode"] || "",
                            "subdistrict_code": data["subdistrictCode"] || "",
                            "relative": data["emergencyContacts"] || [],
                            "payer_plan_id": data["payer"] || null,
                            "session_id": pendingSessionId || null,
                            "nationality": data["nationality"] || "",
                            "preferred_language": data["preferredLanguage"] || "",
                        };
                        try {
                            const response = await createOrUpdatePatient(payload);
                            setPatientId(response.patient_id);
                            const nextScreeningInitialValues = {};
                            if (data["weight"] !== undefined && data["weight"] !== null && data["weight"] !== "") {
                                nextScreeningInitialValues.weight = data["weight"];
                            }
                            if (data["height"] !== undefined && data["height"] !== null && data["height"] !== "") {
                                nextScreeningInitialValues.height = data["height"];
                            }
                            if (data["congenitalDiseaseDetail"] !== undefined) {
                                nextScreeningInitialValues.congenitalDiseaseDetail = data["congenitalDiseaseDetail"];
                            }
                            if (data["hasDrugAllergy"] !== undefined) {
                                nextScreeningInitialValues.hasDrugAllergy = data["hasDrugAllergy"] === "True" ? "yes" : "no";
                            }
                            if (data["drugAllergyDetail"] !== undefined) {
                                nextScreeningInitialValues.drugAllergyDetail = data["drugAllergyDetail"];
                            }
                            if (data["hasSmoking"] !== undefined) {
                                nextScreeningInitialValues.hasSmoking = data["hasSmoking"] === "True" ? "yes" : "no";
                            }
                            if (data["smokingDetail"] !== undefined) {
                                nextScreeningInitialValues.smokingDetail = data["smokingDetail"];
                            }
                            if (data["hasDrinking"] !== undefined) {
                                nextScreeningInitialValues.hasDrinking = data["hasDrinking"] === "True" ? "yes" : "no";
                            }
                            if (data["drinkingDetail"] !== undefined) {
                                nextScreeningInitialValues.drinkingDetail = data["drinkingDetail"];
                            }
                            if (data["isPregnant"] !== undefined) {
                                nextScreeningInitialValues.isPregnant = data["isPregnant"] === "True" ? "yes" : "no";
                            }
                            setScreeningInitialValues(nextScreeningInitialValues);
                            showSuccessToast("บันทึกข้อมูลผู้ป่วยสำเร็จ");
                            onSuccess?.();
                        } catch (error) {
                            showErrorToast("ไม่สามารถเพิ่มข้อมูลผู้ป่วยได้", error);
                            return false;
                        }
                        setIsCreateOrEditPatientModalOpen(false);
                        setIsScreeningModalOpen(true);
                        return true;
                    }}
                />
            </Suspense>
        )}
        <ScreeningModal
            isOpen={isScreeningModalOpen}
            onCancel={() => {
                setIsScreeningModalOpen(false)
                resetRegistrationFlow();
            }}
            patientId={patientId}
            initialValues={screeningInitialValues}
            onSave={async (data) => {
                const payload = buildScreeningPayload({
                    patientId,
                    formData: data,
                    sessionId: pendingSessionId,
                });
                try {
                    await createVisit(payload);
                    showSuccessToast("ลงคิวผู้ป่วยสำเร็จ");
                    onSuccess?.();
                } catch (error) {
                    showErrorToast("ไม่สามารถลงคิวผู้ป่วยได้", error);
                    return;
                }
                setIsScreeningModalOpen(false);
                resetRegistrationFlow();
                navigate("/queue-management");
            }}
            onSaveDraftSuccess={() => {
                resetRegistrationFlow();
                navigate("/queue-management");
            }}
        />
    </div>)
}
